import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { BsInfoCircle, BsPinAngleFill, BsThreeDots } from "react-icons/bs";
import { FaHeart, FaStar } from "react-icons/fa";
import profileimg from "../../assets/profile.png";
import dollar from "../../assets/images/icons/dollar.svg";
import { getStoredToken, getUser } from "../../utils/auth";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
// Live products change seldom, but sold counts move with every purchase
const PRODUCTS_REFRESH_MS = 30000;

export const authHeaders = () => ({ "Content-Type": "application/json", token: getStoredToken() });

export const formatPrice = (value) => `$${Number(value || 0).toFixed(2)}`;

export const formatCountdown = (endsAt, now) => {
  const seconds = Math.max(0, Math.floor((new Date(endsAt).getTime() - now) / 1000));
  return [Math.floor(seconds / 3600), Math.floor((seconds % 3600) / 60), seconds % 60]
    .map((part) => String(part).padStart(2, "0"))
    .join(":");
};

// Comment text is HTML-escaped by the API
const decodeHtml = (text) => new DOMParser().parseFromString(text || "", "text/html").documentElement.textContent;

export const useNow = () => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  return now;
};

export const useLiveComments = (streamId, socket) => {
  const [comments, setComments] = useState([]);

  useEffect(() => {
    setComments([]);
    if (!streamId) return;
    fetch(`${API_BASE_URL}/streams/${streamId}/comments?limit=50`, { headers: authHeaders() })
      .then((res) => res.json())
      .then((data) => {
        if (!data.success) return;
        const loaded = data.result.comments || [];
        // Comments may already have arrived over the socket while this was loading
        setComments((prev) =>
          [...loaded, ...prev.filter((c) => !loaded.some((l) => l.commentId === c.commentId))].sort(
            (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
          )
        );
      })
      .catch(() => {});
  }, [streamId]);

  useEffect(() => {
    if (!socket || !streamId) return;
    const userId = getUser()?.id;
    const addComment = (comment) =>
      setComments((prev) => (prev.some((c) => c.commentId === comment.commentId) ? prev : [...prev, comment]));
    const removeComment = (payload) =>
      setComments((prev) => prev.filter((c) => c.commentId !== (payload.commentId || payload.comment_id)));
    // Only one comment is pinned at a time
    const markComment = ({ comment_id, action }) =>
      setComments((prev) =>
        prev.map((c) => {
          if (action === "pin") return { ...c, isPinned: c.commentId === comment_id };
          if (c.commentId !== comment_id) return c;
          return action === "unpin" ? { ...c, isPinned: false } : { ...c, isHighlighted: action === "highlight" };
        })
      );
    const markEvents = ["comment-pinned", "comment-unpinned", "comment-highlighted", "comment-unhighlighted"];

    // Rooms belong to the connection, so they are joined again after a reconnect
    const joinRoom = () => socket.emit("join-stream-comments", { streamId, userId });
    joinRoom();
    socket.on("connect", joinRoom);
    socket.on("new-comment", addComment);
    socket.on("comment-deleted", removeComment);
    socket.on("comment-removed", removeComment);
    markEvents.forEach((event) => socket.on(event, markComment));
    return () => {
      socket.emit("leave-stream-comments", { streamId, userId });
      socket.off("connect", joinRoom);
      socket.off("new-comment", addComment);
      socket.off("comment-deleted", removeComment);
      socket.off("comment-removed", removeComment);
      markEvents.forEach((event) => socket.off(event, markComment));
    };
  }, [socket, streamId]);

  const sendComment = async (comment) => {
    const res = await fetch(`${API_BASE_URL}/streams/${streamId}/comments`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({ comment, timestamp: new Date().toISOString() }),
    });
    const data = await res.json();
    if (!res.ok || !data.success) throw new Error(data.message || "Unable to post comment");
  };

  return { comments, sendComment };
};

// Offer end times are shifted by the device's clock difference to the server, so a countdown reaches zero
// exactly when the API stops selling (a device clock a minute slow showed "Unavailable" with time left)
const toDeviceClock = (data) => {
  const offset = data.serverTime ? new Date(data.serverTime).getTime() - Date.now() : 0;
  return (data.products || []).map((product) =>
    product.endsAt && offset ? { ...product, endsAt: new Date(product.endsAt).getTime() - offset } : product
  );
};

export const useLiveProducts = (streamId, socket) => {
  const [products, setProducts] = useState([]);

  const refreshProducts = useCallback(async () => {
    if (!streamId) return;
    try {
      const res = await fetch(`${API_BASE_URL}/live/${streamId}/products`, { headers: authHeaders() });
      const data = await res.json();
      if (res.ok) setProducts(toDeviceClock(data));
    } catch (error) {
      console.error("Error fetching live products:", error);
    }
  }, [streamId]);

  useEffect(() => {
    if (!streamId) {
      setProducts([]);
      return;
    }
    refreshProducts();
    const timer = setInterval(refreshProducts, PRODUCTS_REFRESH_MS);
    return () => clearInterval(timer);
  }, [streamId, refreshProducts]);

  useEffect(() => {
    if (!socket || !streamId) return;
    const userId = getUser()?.id;
    const onProductsUpdated = (payload) => setProducts(toDeviceClock(payload));

    const joinRoom = () => socket.emit("join-stream-products", { sessionId: streamId, userId });
    joinRoom();
    socket.on("connect", joinRoom);
    socket.on("products-updated", onProductsUpdated);
    return () => {
      socket.emit("leave-stream-products", { sessionId: streamId, userId });
      socket.off("connect", joinRoom);
      socket.off("products-updated", onProductsUpdated);
    };
  }, [socket, streamId]);

  return { products, refreshProducts };
};

const LiveComment = ({ comment: c, actions = [] }) => {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className={`border-b border-gray-200 pb-2 ${c.isHighlighted ? "rounded-lg bg-[#FFF6D6] px-2 pt-1" : ""}`}>
      <div className="flex items-center gap-2">
        <img src={c.userProfilePicture || profileimg} alt="" className="w-7 h-7 rounded-full object-cover" />
        <div className="min-w-0">
          <p className="text-xs font-semibold">
            {c.userFullName?.trim() || c.username || "User"}
            {c.username && <span className="ml-1 font-normal text-gray-500">@{c.username}</span>}
          </p>
          <p className="text-[10px] text-gray-400">
            {new Date(c.timestamp).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}
          </p>
        </div>
        {c.amount > 0 && (
          <span className="ml-auto flex items-center gap-1 rounded-full border border-[#FF7A51] bg-[#FFE7A3] px-2 text-xs font-semibold">
            <img src={dollar} alt="" className="h-3 w-3" />
            {c.amount}
          </span>
        )}
        {actions.length > 0 && (
          <button
            onClick={() => setMenuOpen((open) => !open)}
            title="Moderate"
            className="ml-auto rounded-full p-1 hover:bg-gray-200"
          >
            <BsThreeDots />
          </button>
        )}
      </div>
      <p className="text-xs pt-1 break-words">{decodeHtml(c.comment)}</p>
      {/* Shown inline: a dropdown would be clipped by the scrolling comment list */}
      {menuOpen && actions.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1">
          {actions.map((action) => (
            <button
              key={action.label}
              onClick={() => {
                setMenuOpen(false);
                action.onClick();
              }}
              className="rounded-full border border-gray-300 bg-white px-3 py-[2px] text-[11px] hover:bg-gray-100"
            >
              {action.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Comments carrying an `amount` (donations and gifts) are highlighted with an amount badge.
// `getActions(comment)` adds a moderation menu; `onLike` adds a like button with the `likes` count.
export const LiveCommentsPanel = ({
  comments,
  chatEnabled,
  onSend,
  className = "",
  disabledPlaceholder = "Comments are turned off",
  getActions,
  likes,
  onLike,
}) => {
  const [comment, setComment] = useState("");
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);
  const pinned = comments.find((c) => c.isPinned);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [comments]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!comment.trim() || sending) return;
    setSending(true);
    try {
      await onSend(comment.trim());
      setComment("");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={`flex flex-col ${className}`}>
      {pinned && (
        <div className="mb-2 rounded-lg border border-[#095228] bg-white p-2">
          <p className="mb-1 flex items-center gap-1 text-[10px] font-semibold text-[#095228]">
            <BsPinAngleFill /> Pinned
          </p>
          <LiveComment comment={pinned} actions={getActions?.(pinned)} />
        </div>
      )}
      <div ref={listRef} className="flex-1 overflow-y-auto space-y-3 pr-1 scrollbar-hidden">
        {comments.length === 0 && <p className="text-sm text-gray-400 text-center pt-6">No comments yet</p>}
        {comments.filter((c) => c !== pinned).map((c) => (
          <LiveComment key={c.commentId} comment={c} actions={getActions?.(c)} />
        ))}
      </div>
      <div className="mt-3 flex items-center gap-2">
        <form onSubmit={handleSend} className="relative flex-1">
          <input
            type="text"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            maxLength={1000}
            disabled={!chatEnabled}
            placeholder={chatEnabled ? "Add a comment" : disabledPlaceholder}
            className="w-full rounded-full border border-gray-300 bg-white py-2 pl-4 pr-20 text-sm focus:outline-none disabled:bg-gray-100"
          />
          <button
            type="submit"
            disabled={!chatEnabled || !comment.trim() || sending}
            className="absolute right-1 top-1 rounded-full bg-gradient-to-r from-[#08AEEA] to-[#1DE78B] px-4 py-1 text-sm text-white disabled:opacity-50"
          >
            Send
          </button>
        </form>
        {onLike && (
          <button onClick={onLike} title="Like" className="flex items-center gap-1 text-[#BE1F2E]">
            <FaHeart className="text-xl" />
            <span className="text-xs font-semibold text-black">{likes}</span>
          </button>
        )}
      </div>
    </div>
  );
};

// Results of a live the host just ended: host-only analytics, reactions and, for live shopping, units sold
export const LiveSummary = ({ streamId, streamType, startedAt, endedAt }) => {
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    const load = async (path) => {
      const res = await fetch(`${API_BASE_URL}/live/${streamId}${path}`, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      return data;
    };
    Promise.all([load("/analytics"), load("/engagement"), streamType === "marketplace" ? load("/products") : null])
      .then(([analytics, engagement, productData]) => {
        if (!active) return;
        setStats({ ...analytics.result, ...engagement.result?.engagement });
        setProducts(productData?.products || []);
      })
      .catch(() => active && setFailed(true));
    return () => {
      active = false;
    };
  }, [streamId, streamType]);

  const tiles = stats && [
    { label: "Duration", value: formatCountdown(endedAt, new Date(startedAt).getTime()) },
    { label: "Peak Viewers", value: stats.peak_viewers },
    { label: "New Followers", value: stats.new_followers },
    { label: "Likes", value: stats.likeCount },
    { label: "Reactions", value: stats.totalReactions },
    { label: "Gifts", value: `${stats.totalGifts} (${stats.gift_value} coins)` },
    ...(streamType === "donation"
      ? [{ label: "Donations", value: `${formatPrice(stats.donation_value)} (${stats.donation_count})` }]
      : [{ label: "Units Sold", value: products.reduce((sum, product) => sum + (product.soldQuantity || 0), 0) }]),
  ];

  return (
    <section className="mt-4 rounded-xl border border-gray-200 bg-white p-4">
      <h2 className="font-semibold text-[#095228]">Live Summary</h2>
      <p className="text-xs text-gray-500">Your live has ended. These results stay here until you leave this page.</p>
      {failed && <p className="mt-3 text-sm text-[#BE1F2E]">Unable to load the live results</p>}
      {!failed && !tiles && <p className="mt-3 text-sm text-gray-400">Loading...</p>}
      {tiles && (
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {tiles.map((tile) => (
            <div key={tile.label} className="rounded-lg bg-[#EBF8ED] p-3">
              <p className="text-xs text-gray-500">{tile.label}</p>
              <p className="text-lg font-semibold">{tile.value}</p>
            </div>
          ))}
        </div>
      )}
      {products.length > 0 && (
        <div className="mt-4 space-y-1">
          <p className="text-sm font-semibold">Products</p>
          {products.map((product) => (
            <div key={product.liveStreamProductId} className="flex justify-between gap-3 text-sm">
              <span className="truncate">{product.name}</span>
              <span className="shrink-0 font-semibold">{product.soldQuantity} sold</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

// Info icon with a short note: hover on desktop, tap on touch screens (a tap outside closes it)
export const InfoTip = ({ text, label = "More info" }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const close = (e) => !ref.current?.contains(e.target) && setOpen(false);
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, [open]);

  return (
    <span ref={ref} className="relative inline-flex" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button type="button" onClick={() => setOpen(true)} aria-label={label} className="text-gray-500">
        <BsInfoCircle />
      </button>
      {open && (
        <span
          role="tooltip"
          className="absolute bottom-full right-0 z-30 mb-2 w-60 rounded-lg bg-[#161F28] px-3 py-2 text-xs font-normal text-white shadow-lg"
        >
          {text}
        </span>
      )}
    </span>
  );
};

// A live of this tab left running by a page reload (useLiveBroadcast's pendingLive)
const LIVE_ENDING_WARNING_SECONDS = 5 * 60;
const LIVE_ENDING_URGENT_SECONDS = 60;

// Host studios only: the last minutes of the host plan's time for this live (`sessionLimit` from useLiveBroadcast),
// shown above everything, full screen included, before the server ends the live
export const LiveTimeLimitWarning = ({ sessionLimit, now }) => {
  const secondsLeft = sessionLimit ? Math.max(0, Math.ceil((sessionLimit.endsAt - now) / 1000)) : null;
  const showWarning = secondsLeft !== null && secondsLeft <= LIVE_ENDING_WARNING_SECONDS;
  const toastedRef = useRef(false);

  useEffect(() => {
    if (!showWarning) {
      toastedRef.current = false;
      return;
    }
    if (toastedRef.current) return;
    toastedRef.current = true;
    toast.warning(`Your live will end in ${Math.max(1, Math.ceil(secondsLeft / 60))} min. Wrap up soon!`);
  }, [showWarning, secondsLeft]);

  if (!showWarning) return null;
  return (
    <div
      role="alert"
      className={`fixed left-1/2 top-4 z-[1100] w-[calc(100%-2rem)] max-w-md -translate-x-1/2 rounded-xl px-4 py-3 text-center text-white shadow-lg ${
        secondsLeft <= LIVE_ENDING_URGENT_SECONDS ? "bg-[#BE1F2E] animate-pulse" : "bg-[#F7A440]"
      }`}
    >
      <p className="text-base font-semibold">
        {secondsLeft > 0 ? `Your live ends in ${formatCountdown(sessionLimit.endsAt, now)}` : "Ending your live..."}
      </p>
      <p className="mt-0.5 text-xs">
        {sessionLimit.minutes
          ? `Your plan allows ${sessionLimit.minutes} minutes per live. It will end automatically.`
          : "It will end automatically."}
      </p>
    </div>
  );
};

export const PendingLiveBanner = ({ title, busy, canResume, onResume, onEnd }) => (
  <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#BE1F2E] bg-[#FDECEE] px-4 py-3">
    <p className="text-sm font-semibold">
      Your live{title ? ` "${title}"` : ""} is still running. Resume broadcasting or end it.
    </p>
    <div className="flex gap-2">
      <button
        onClick={onResume}
        disabled={busy || !canResume}
        className="rounded-full bg-[#095228] px-5 py-1.5 text-sm font-semibold text-white disabled:opacity-60"
      >
        Resume Live
      </button>
      <button
        onClick={onEnd}
        disabled={busy}
        className="rounded-full border border-[#BE1F2E] px-5 py-1.5 text-sm font-semibold text-[#BE1F2E] disabled:opacity-60"
      >
        End Live
      </button>
    </div>
  </div>
);

// The seller can feature one live product; the API lists it first
export const FeaturedBadge = ({ className = "" }) => (
  <span className={`flex items-center gap-1 rounded-full bg-[#095228] px-2 py-[2px] text-[9px] font-semibold text-white ${className}`}>
    <FaStar className="text-[8px]" /> Featured
  </span>
);

export const LiveProductCard = ({ product, now, children, className = "" }) => {
  const hasLivePrice = product.livePrice !== null && product.livePrice !== undefined;
  const salePrice = hasLivePrice ? product.livePrice : product.discountedPrice;

  return (
    <div className={`rounded-xl border bg-white p-1 ${product.isFeatured ? "border-2 border-[#095228]" : ""} ${className}`}>
      <div className="relative rounded-xl bg-[#ECEEF0]">
        {product.isFeatured && <FeaturedBadge className="absolute right-1 top-1" />}
        {product.offerPercentage > 0 && (
          <span className="absolute left-0 top-0 rounded-br-xl rounded-tl-xl bg-[#BE1F2E] px-2 py-1 text-[10px] font-semibold text-white">
            {product.offerPercentage}% Offer
          </span>
        )}
        <img
          src={product.thumbnailUrl || "/image/default.png"}
          alt={product.name}
          className="h-32 w-full object-contain"
        />
        {product.endsAt && (
          <span className="absolute bottom-1 right-2 rounded-xl bg-[#98911F] px-2 py-[2px] text-[10px] text-white">
            {formatCountdown(product.endsAt, now)}
          </span>
        )}
      </div>
      <div className="space-y-1 p-2">
        <p className="line-clamp-2 text-xs font-bold uppercase">{product.name}</p>
        <p className="text-sm font-bold text-[#095228]">
          {salePrice < product.price && (
            <span className="mr-2 text-[11px] font-semibold text-gray-400 line-through">{formatPrice(product.price)}</span>
          )}
          {formatPrice(salePrice)}
        </p>
        {children}
      </div>
    </div>
  );
};
